import React, { useState, useEffect } from "react";
import {
  PriceAlertConfigData,
  CreatePriceAlertConfigPayload,
  UpdatePriceAlertConfigPayload,
} from "@/api/price-alerts";

interface PriceAlertConfigFormContentProps {
  config?: PriceAlertConfigData | null;
  onSubmit: (
    payload: CreatePriceAlertConfigPayload | UpdatePriceAlertConfigPayload
  ) => void;
  isLoading?: boolean;
}

const TIMEFRAME_OPTIONS = ["1m", "5m", "15m", "30m", "1h", "4h"];

export const PriceAlertConfigFormContent: React.FC<
  PriceAlertConfigFormContentProps
> = ({ config, onSubmit, isLoading }) => {
  const isEdit = !!config;

  const [exchange, setExchange] = useState(
    config?.exchange || "binance_futures"
  );
  const [tradingPair, setTradingPair] = useState(config?.tradingPair || "");
  const [alertType, setAlertType] = useState<"SPIKE" | "DROP">(
    config?.alertType || "SPIKE"
  );
  const [changeThreshold, setChangeThreshold] = useState(
    config?.changeThreshold ?? 5
  );
  const [timeframe, setTimeframe] = useState(config?.timeframe || "5m");
  const [status, setStatus] = useState<"ACTIVE" | "DISABLED">(
    config?.status || "ACTIVE"
  );

  useEffect(() => {
    if (config) {
      setExchange(config.exchange);
      setTradingPair(config.tradingPair || "");
      setAlertType(config.alertType);
      setChangeThreshold(config.changeThreshold);
      setTimeframe(config.timeframe);
      setStatus(config.status);
    }
  }, [config]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const threshold = changeThreshold;
    if (threshold < 5 || threshold > 15) return;

    if (isEdit) {
      const payload: UpdatePriceAlertConfigPayload = {
        exchange,
        tradingPair: tradingPair || null,
        alertType,
        changeThreshold: threshold,
        timeframe,
        status,
      };
      onSubmit(payload);
    } else {
      const payload: CreatePriceAlertConfigPayload = {
        exchange,
        tradingPair: tradingPair || undefined,
        alertType,
        changeThreshold: threshold,
        timeframe,
      };
      onSubmit(payload);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Exchange */}
      <div className="rounded-xl bg-black/30 p-4 border border-gray-500/20 space-y-2">
        <label
          htmlFor="pa-exchange"
          className="text-xs uppercase tracking-wide text-text-secondary"
        >
          Exchange
        </label>
        <select
          id="pa-exchange"
          value={exchange}
          onChange={(e) => setExchange(e.target.value)}
          className="w-full bg-primary/40 border border-gray-600 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-accent"
        >
          <option value="binance_futures">Binance Futures</option>
          <option value="gate_futures">Gate Futures</option>
        </select>
      </div>

      {/* Trading Pair */}
      <div className="rounded-xl bg-black/30 p-4 border border-gray-500/20 space-y-2">
        <label
          htmlFor="pa-pair"
          className="text-xs uppercase tracking-wide text-text-secondary"
        >
          Trading Pair (leave empty for all)
        </label>
        <input
          id="pa-pair"
          type="text"
          value={tradingPair}
          onChange={(e) => setTradingPair(e.target.value.toUpperCase())}
          placeholder="e.g. BTCUSDT"
          className="w-full bg-primary/40 border border-gray-600 rounded-lg px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary/50 focus:outline-none focus:border-accent"
        />
      </div>

      {/* Alert Type */}
      <div className="rounded-xl bg-black/30 p-4 border border-gray-500/20 space-y-2">
        <span className="text-xs uppercase tracking-wide text-text-secondary">
          Direction
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setAlertType("SPIKE")}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              alertType === "SPIKE"
                ? "bg-green-600 text-white"
                : "bg-primary/40 border border-gray-600 text-text-secondary hover:bg-gray-600/30"
            }`}
          >
            Spike
          </button>
          <button
            type="button"
            onClick={() => setAlertType("DROP")}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              alertType === "DROP"
                ? "bg-red-600 text-white"
                : "bg-primary/40 border border-gray-600 text-text-secondary hover:bg-gray-600/30"
            }`}
          >
            Drop
          </button>
        </div>
      </div>

      {/* Change Threshold */}
      <div className="rounded-xl bg-black/30 p-4 border border-gray-500/20 space-y-3">
        <div className="flex items-center justify-between">
          <label
            htmlFor="pa-threshold"
            className="text-xs uppercase tracking-wide text-text-secondary"
          >
            Change Threshold
          </label>
          <span className="text-sm font-semibold text-accent">
            {changeThreshold}%
          </span>
        </div>
        <input
          id="pa-threshold"
          type="range"
          min={5}
          max={15}
          step={1}
          value={changeThreshold}
          onChange={(e) => setChangeThreshold(Number(e.target.value))}
          className="w-full accent-accent cursor-pointer"
        />
        <div className="flex justify-between text-xs text-text-secondary/60">
          <span>5%</span>
          <span>15%</span>
        </div>
      </div>

      {/* Timeframe */}
      <div className="rounded-xl bg-black/30 p-4 border border-gray-500/20 space-y-2">
        <span className="text-xs uppercase tracking-wide text-text-secondary">
          Timeframe
        </span>
        <div className="grid grid-cols-3 gap-2">
          {TIMEFRAME_OPTIONS.map((tf) => (
            <button
              key={tf}
              type="button"
              onClick={() => setTimeframe(tf)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                timeframe === tf
                  ? "bg-accent text-white"
                  : "bg-primary/40 border border-gray-600 text-text-secondary hover:bg-gray-600/30"
              }`}
            >
              {tf}
            </button>
          ))}
        </div>
      </div>

      {/* Status (edit only) */}
      {isEdit && (
        <div className="rounded-xl bg-black/30 p-4 border border-gray-500/20 space-y-2">
          <span className="text-xs uppercase tracking-wide text-text-secondary">
            Status
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setStatus("ACTIVE")}
              className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                status === "ACTIVE"
                  ? "bg-green-600 text-white"
                  : "bg-primary/40 border border-gray-600 text-text-secondary hover:bg-gray-600/30"
              }`}
            >
              Active
            </button>
            <button
              type="button"
              onClick={() => setStatus("DISABLED")}
              className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                status === "DISABLED"
                  ? "bg-yellow-600 text-white"
                  : "bg-primary/40 border border-gray-600 text-text-secondary hover:bg-gray-600/30"
              }`}
            >
              Disabled
            </button>
          </div>
        </div>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading}
        className="w-full px-4 py-3 rounded-lg bg-accent hover:bg-accent/80 text-white font-semibold transition-colors disabled:opacity-50"
      >
        {isLoading && "Saving..."}
        {!isLoading && isEdit && "Save Changes"}
        {!isLoading && !isEdit && "Create Alert"}
      </button>
    </form>
  );
};
